import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Hourglass } from 'lucide-react'
import { EndpointHint, HelpIcon, LoadFailure, Skeleton, type AdminSession } from '../../../core/index.js'
import type { FeedbackAdminApi } from '../../adminApi.js'
import { formatWhen } from '../../components/FeedbackStatus.js'
import { CHANGE_REQUEST_STATUSES, type ChangeRequestView } from '../../types.js'
import { ChangeStatusChip } from './ChangeParts.js'
import { ChangeDetail } from './ChangeDetail.js'
import { CHANGE_STATUS_LABEL, changeTitle, summarizeChanges, type ChangeSummary } from './changeText.js'
import { CHANGES_QUERY_KEY, useSelectedChange } from './useChangeStep.js'

type AdminApi = FeedbackAdminApi

function Tile({ label, value, note, emphasis = false }: { label: string; value: number; note?: string; emphasis?: boolean }) {
  return (
    <div className={`border bg-white px-4 py-3 ${emphasis ? 'border-coral/60' : 'border-marine/15'}`}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${emphasis ? 'text-coral' : 'text-ink'}`}>{value}</div>
      {note && <div className="text-[11px] text-ink-mid">{note}</div>}
    </div>
  )
}

/** The panel the page opens with: how many changes, how many still moving, and what only the root can move on. */
function ChangesSummary({ summary, isRoot }: { summary: ChangeSummary; isRoot: boolean }) {
  return (
    <section className="border border-marine/15 bg-marine-lt/20 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile label="Changes" value={summary.total} note={`${summary.open} still moving`} />
        <Tile label="Waiting on the root" value={summary.waitingOnRoot} note={isRoot ? 'Proposed or in review: yours to decide' : 'Proposed or in review'} emphasis={isRoot && summary.waitingOnRoot > 0} />
        <Tile label="Approved, to run" value={summary.byStatus.approved} note="Prompt ready, no pull request yet" />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-mid">
        {CHANGE_REQUEST_STATUSES.map((status) => (
          <span key={status}>
            {CHANGE_STATUS_LABEL[status]} <span className="font-bold text-ink">{summary.byStatus[status]}</span>
          </span>
        ))}
      </div>
    </section>
  )
}

function ChangeList({ changes, onOpen }: { changes: readonly ChangeRequestView[]; onOpen: (changeRequestId: string) => void }) {
  if (changes.length === 0) {
    return <div className="border border-marine/15 bg-white p-6 text-sm text-ink-mid">No changes yet. Develop an open item on the Feedback page to start one.</div>
  }
  return (
    <ul className="border border-marine/15 bg-white">
      {changes.map((change) => (
        <li key={change.changeRequestId} className="border-b border-marine/10 last:border-b-0">
          <button type="button" onClick={() => onOpen(change.changeRequestId)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-marine-lt/30">
            <ChangeStatusChip status={change.status} />
            <span className="min-w-0 flex-1 text-xs font-bold text-ink">{changeTitle(change)}</span>
            <span className="text-[11px] text-ink-mid">
              {change.feedbackIds.length} {change.feedbackIds.length === 1 ? 'feedback item' : 'feedback items'}
            </span>
            <span className="text-[11px] text-ink-mid">Updated {formatWhen(change.updatedAt)}</span>
            <ChevronRight aria-hidden="true" className="h-4 w-4 text-ink-mid" />
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * Feedback becoming a change (docs/plans/feedback-to-change-plan.md, phase
 * 0). Every admin sees every change and writes the requirement while it is
 * a draft; approving it to send, recording what came back and accepting it
 * are the root's alone, and everyone else sees where it waits on the root.
 */
export function ChangesPage({ api, session }: { api: AdminApi; session: AdminSession }) {
  const isRoot = session.role === 'root'
  const [selected, select] = useSelectedChange()
  const listQuery = useQuery({ queryKey: CHANGES_QUERY_KEY, queryFn: api.listChanges })
  const summary = useMemo(() => summarizeChanges(listQuery.data ?? []), [listQuery.data])

  return (
    <div className="max-w-5xl mx-auto px-7 py-6 flex flex-col gap-5">
      <div className="flex items-start gap-1">
        <p className="max-w-2xl text-xs leading-relaxed text-ink-mid">
          Feedback an admin accepted for development, written up as a requirement. The root admin approves the exact version to hand to Claude, runs it, records the
          pull request that comes back and accepts it at the commit reviewed.
        </p>
        <HelpIcon topic="feedback.changes" />
      </div>

      {listQuery.isPending ? (
        <Skeleton lines={5} label="Loading changes" />
      ) : listQuery.isError ? (
        <LoadFailure message="Could not load the changes. If the API was deployed before this page existed, deploy it to switch this on." onRetry={() => void listQuery.refetch()} retrying={listQuery.isFetching} />
      ) : (
        <>
          <ChangesSummary summary={summary} isRoot={isRoot} />
          {!isRoot && summary.waitingOnRoot > 0 && (
            <p className="flex items-center gap-1.5 text-xs text-ink-mid">
              <Hourglass aria-hidden="true" className="h-3.5 w-3.5" />
              {summary.waitingOnRoot} waiting on the root admin.
            </p>
          )}
        </>
      )}

      {selected ? (
        <ChangeDetail api={api} changeRequestId={selected} isRoot={isRoot} onBack={() => select(null)} />
      ) : (
        listQuery.isSuccess && (
          <section>
            <div className="mb-2 flex items-baseline gap-2 text-xs font-extrabold text-ink">
              All changes · {listQuery.data.length}
              <EndpointHint>GET /admin/changes</EndpointHint>
            </div>
            <ChangeList changes={listQuery.data} onOpen={select} />
          </section>
        )
      )}
    </div>
  )
}
